import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import QrCode from '@/components/ui/QrCode';
import '../loyalty.css';
import CopyableCode from './CopyableCode';

interface Props {
  open: boolean;
  /** What the QR encodes (`OTL:<code>`). */
  payload: string;
  /** The code behind it, shown grouped under the QR for staff who type it. */
  code: string;
  /** Card or coupon name — the accessible name of the QR. */
  title: string;
  onClose: () => void;
}

// The Screen Wake Lock API is not typed on every TS lib target we build with.
type WakeLockSentinelLike = { release: () => Promise<void> };
type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
};

/**
 * The QR full screen, white, for staff to scan (SC-1091, review M7): at 192 px
 * the code itself was ~139 px once the quiet zone is out. Deliberately not
 * themed — a scanner needs black on white — and above every other layer (the
 * sakura petals sit at z-100 and used to drift over the code).
 */
export default function QrSheet({ open, payload, code, title, onClose }: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Keep the screen on while the staff scans. Best effort: unsupported (older
    // iOS), refused (low battery) or a released lock must never break the sheet.
    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;
    try {
      const request = (navigator as WakeLockNavigator).wakeLock?.request('screen');
      request
        ?.then((s) => {
          if (cancelled) void s.release().catch(() => {});
          else sentinel = s;
        })
        .catch(() => {});
    } catch {
      /* wake lock unavailable */
    }

    return () => {
      cancelled = true;
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
      void sentinel?.release().catch(() => {});
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="loyalty-qr-sheet"
      className="loyalty-scope fixed inset-0 z-[1100] flex flex-col items-center justify-center gap-4 bg-white px-6 py-6 text-black"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute end-3 top-3 min-h-[48px] rounded-btn-sm border-2 border-solid border-black bg-white px-4 font-display text-sm text-black"
      >
        {t('loyalty.qrSheet.close')} ✕
      </button>
      <div className="w-[min(92vw,60vh,480px)]">
        <QrCode payload={payload} size={460} label={title} className="block h-auto w-full" />
      </div>
      <CopyableCode code={code} codeClassName="text-3xl text-black" tone="black" />
      <p className="m-0 max-w-[32ch] text-center text-base text-black/70">{t('loyalty.qrSheet.hint')}</p>
    </div>,
    document.body,
  );
}
